// Handling for the `deriv` URL parameter (derivative view of timeseries).
// See doc/availableURLParameters.md and doc/specs/2026-06-30-deriv-url-parameter.md.

export type DerivMode = "centered" | "forward"

const CENTERED_VALUES = ["Centered", "centered", "C", "c"]
const FORWARD_VALUES = ["Forward", "forward", "F", "f"]
const CUMUL_ACTIVE_VALUES = ["Yes", "yes", "Y", "y"]

function isCumulActive(urlParams: URLSearchParams): boolean {
  const raw = urlParams.get("cumul")
  return raw !== null && CUMUL_ACTIVE_VALUES.includes(raw)
}

/**
 * Whether the raw `deriv` parameter holds a recognized active value, ignoring
 * the cumul/deriv mutual-exclusion rule. Used by cumulParam to detect a clash.
 */
export function isDerivParamActive(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  const raw = new URLSearchParams(window.location.search).get("deriv")
  return raw !== null && (CENTERED_VALUES.includes(raw) || FORWARD_VALUES.includes(raw))
}

/**
 * Resolve the active derivative mode from the current URL, or null if the
 * `deriv` parameter is unset / `No` / an unrecognized value. `deriv` cannot be
 * combined with `cumul`: if both are active simultaneously, both are ignored.
 */
export function getDerivMode(): DerivMode | null {
  if (typeof window === "undefined") {
    return null
  }
  const urlParams = new URLSearchParams(window.location.search)
  const raw = urlParams.get("deriv")
  if (raw === null) {
    return null
  }
  let mode: DerivMode | null = null
  if (CENTERED_VALUES.includes(raw)) {
    mode = "centered"
  } else if (FORWARD_VALUES.includes(raw)) {
    mode = "forward"
  }
  if (mode === null) {
    return null
  }
  if (isCumulActive(urlParams)) {
    return null
  }
  return mode
}

/**
 * Insert `|derivative` before the last ` (` in a string (scanning from the end).
 * If no ` (` is present, append it at the end.
 */
function insertDerivativeTag(text: string): string {
  const idx = text.lastIndexOf(" (")
  if (idx === -1) {
    return text + "|derivative"
  }
  return text.slice(0, idx) + "|derivative" + text.slice(idx)
}

/**
 * y-axis label transform: insert `|derivative` before the last ` (` and `/year`
 * before the final `)`. No-op when `deriv` is inactive.
 */
export function applyDerivToYLabel(label: string): string {
  if (getDerivMode() === null) {
    return label
  }
  let result = insertDerivativeTag(label)
  const closingIdx = result.lastIndexOf(")")
  if (closingIdx !== -1) {
    result = result.slice(0, closingIdx) + "/year" + result.slice(closingIdx)
  } else {
    result = result + "/year"
  }
  return result
}

/** Chart title transform: insert `|derivative` before the last ` (`. */
export function applyDerivToTitle(title: string): string {
  if (getDerivMode() === null) {
    return title
  }
  return insertDerivativeTag(title)
}

/** Legend SubVariable transform: append `|centered-derivative` / `|forward-derivative`. */
export function applyDerivToSubVar(name: string): string {
  const mode = getDerivMode()
  if (mode === null) {
    return name
  }
  return name + "|" + mode + "-derivative"
}
